/**
 * Shared HTTP-error-body normalizer for every REST caller against this
 * backend (`src/lib/api/*`, plus feature hooks with their own inline fetch
 * wrapper — see `useConversations.ts`'s `conversationFetch`). Lives here
 * because `src/lib/api/` is already the established home for this backend's
 * REST clients (feedback.ts, support-requests.ts) — not under a
 * feature-specific module, and not imported sideways from one call site
 * across feature boundaries. Originally written inline in
 * support-requests.ts for the tool-timeout capture flow's 422s; moved here
 * once the identical `error.detail || fallback` bug turned up, independently,
 * at every OTHER call site against this same backend (feedback.ts,
 * useConversations.ts) — every caller was hand-rolling the same two-line
 * check rather than sharing one.
 */

/**
 * Normalises FastAPI's error-response shapes into one readable string.
 * FastAPI's `detail` is a plain string for a hand-raised `HTTPException`,
 * but an ARRAY of `{loc, msg, type}` objects for a 422 Pydantic validation
 * error — `error.detail || fallback` treated both the same, so a 422's
 * array sailed straight into `new Error(anArrayOfObjects)`, which
 * stringifies via `Array.prototype.toString` to the literal text
 * "[object Object]" (or a comma-joined run of them) rendered verbatim to
 * the user. The request itself was never silent (it correctly failed, and
 * any user-typed content stayed intact wherever a caller already preserved
 * it) — only the MESSAGE was useless.
 *
 * Deliberately generic — normalises the SHAPE of the error body, not its
 * wording. Every call site keeps its OWN `fallback` string (distinct,
 * feature-specific copy for the "body had no usable detail at all" case);
 * this function never invents or homogenises that text.
 */
export function extractErrorMessage(body: unknown, fallback: string): string {
  if (typeof body !== 'object' || body === null || !('detail' in body)) {
    return fallback;
  }
  const { detail } = body as { detail: unknown };
  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item) =>
        item && typeof item === 'object' && typeof item.msg === 'string'
          ? (item.msg as string)
          : null
      )
      .filter((msg): msg is string => msg !== null);
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }
  return fallback;
}
